// ─────────────────────────────────────────────────────────────────────────────
// Footer.tsx — Site footer
// Displays active framework/architecture indicators and links.
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom';
import { Github, Linkedin, Globe, Code2, Server } from 'lucide-react';
import { useAppSelector } from '@/hooks';
import { getLinkedinHref } from '@/config/contactLinks';

export function Footer() {
  const activePattern  = useAppSelector((s) => s.ui.frontEndPattern);
  const activeBackend  = useAppSelector((s) => s.ui.backEndArchitecture);
  const personal       = useAppSelector((s) => s.cv.data?.personal);
  const year           = new Date().getFullYear();

  return (
    <footer
      className="text-slate-400 border-t border-white/10"
      style={{ backgroundColor: 'var(--color-hero)' }}
    >
      <div className="section-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

          {/* Branding */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-navy-900 text-sm font-black">JC</span>
              <span className="font-display font-bold text-white text-lg">John Cronogue</span>
            </div>
            <p className="text-sm leading-relaxed">
              Senior Software Engineer. Full-stack solutions across .NET, React, and cloud-native architectures.
            </p>
            <div className="flex items-center gap-4 mt-5">
              <a href={personal ? `https://${personal.github}` : undefined} target="_blank" rel="noopener noreferrer"
                className="hover-text-accent transition-colors" aria-label="GitHub">
                <Github size={18} />
              </a>
              <a href={personal ? getLinkedinHref(personal.linkedin) : undefined} target="_blank" rel="noopener noreferrer"
                className="hover-text-accent transition-colors" aria-label="LinkedIn">
                <Linkedin size={18} />
              </a>
              <a href="/" className="hover-text-accent transition-colors" aria-label="Website">
                <Globe size={18} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Pages</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/"          className="hover-text-accent transition-colors">CV</Link></li>
              <li><Link to="/patterns"  className="hover-text-accent transition-colors">Design Patterns</Link></li>
              <li><Link to="/docker"    className="hover-text-accent transition-colors">Docker Setup</Link></li>
              <li><Link to="/projects"  className="hover-text-accent transition-colors">Projects</Link></li>
            </ul>
          </div>

          {/* Active Stack Indicators */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Active Stack</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Code2 size={15} className="text-accent shrink-0" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">FE Pattern</p>
                  <p className="text-sm text-white font-medium capitalize">{activePattern.replace('-', ' ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Server size={15} className="text-accent shrink-0" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">BE Architecture</p>
                  <p className="text-sm text-white font-medium capitalize">{activeBackend.replace('-', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
          <p>© {year} John Cronogue. All rights reserved.</p>
          <p className="font-mono text-slate-500">
            React + TypeScript + Redux · .NET 8 · MongoDB
          </p>
        </div>
      </div>
    </footer>
  );
}
